import {
  computed,
  DestroyRef,
  Directive,
  inject,
  input,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, NgControl, Validators } from '@angular/forms';
import { FormField } from '@angular/forms/signals';
import { FORM_FIELD_CONTEXT } from './form-field/form-field-context';

let nextInputId = 0;

@Directive({
  selector: '[fcInput]',
  host: {
    class: 'fc-input',
    '[id]': 'id()',
    '[attr.aria-invalid]': 'isErrorShown() || null',
    '[attr.aria-required]': 'isRequired() || null',
    '[attr.aria-describedby]': 'formFieldContext?.describedByIds() ?? null',
  },
  exportAs: 'fcInput',
})
export class InputDirective implements OnInit {
  readonly id = input(`fc-input-${nextInputId++}`);

  private readonly ngControl = inject(NgControl, { optional: true });
  private readonly signalFormField = inject(FormField, {
    optional: true,
    self: true,
  });
  protected readonly formFieldContext = inject(FORM_FIELD_CONTEXT, {
    optional: true,
  });
  private readonly destroyRef = inject(DestroyRef);

  private readonly reactiveControl = signal<AbstractControl | null>(null);
  private readonly reactiveControlVersion = signal(0);

  readonly hasFormControl = computed(
    () => !!this.signalFormField || !!this.reactiveControl(),
  );

  readonly isRequired = computed(() => {
    if (this.signalFormField) return this.signalFormField.state().required();
    this.reactiveControlVersion();
    return this.reactiveControl()?.hasValidator(Validators.required) ?? false;
  });

  readonly isInvalidAndTouched = computed(() => {
    if (this.signalFormField) {
      const fieldState = this.signalFormField.state();
      return fieldState.touched() && fieldState.invalid();
    }
    this.reactiveControlVersion();
    const control = this.reactiveControl();
    return !!control && control.touched && control.invalid;
  });

  readonly signalFormErrors = computed(
    () => this.signalFormField?.errors() ?? [],
  );

  protected readonly isErrorShown = computed(
    () =>
      this.isInvalidAndTouched() ||
      (this.formFieldContext?.isErrorShown() ?? false),
  );

  ngOnInit() {
    const control = this.ngControl?.control;
    if (!(control instanceof AbstractControl)) return;

    this.reactiveControl.set(control);
    control.events
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() =>
        this.reactiveControlVersion.update((version) => version + 1),
      );
  }
}
